/**
 * Keyboard overlay state.
 *
 * Module-level Valtio store for the two app-wide keyboard overlays (command
 * palette, cheat sheet). Only `KeyboardHost` subscribes, and it lazy-mounts each
 * overlay from these flags, so an overlay costs nothing while closed. Mirrors the
 * `pendingSavesStore` pattern: a proxy plus a frozen actions object.
 */

import { proxy } from 'valtio';

export type KeyboardOverlayState = {
  paletteOpen: boolean;
  cheatSheetOpen: boolean;
};

export const keyboardOverlayState = proxy<KeyboardOverlayState>({
  paletteOpen: false,
  cheatSheetOpen: false,
});

const openPalette = () => {
  keyboardOverlayState.paletteOpen = true;
  keyboardOverlayState.cheatSheetOpen = false;
};

const closePalette = () => {
  keyboardOverlayState.paletteOpen = false;
};

const togglePalette = () => {
  keyboardOverlayState.paletteOpen = !keyboardOverlayState.paletteOpen;
  if (keyboardOverlayState.paletteOpen) keyboardOverlayState.cheatSheetOpen = false;
};

const toggleCheatSheet = () => {
  keyboardOverlayState.cheatSheetOpen = !keyboardOverlayState.cheatSheetOpen;
  if (keyboardOverlayState.cheatSheetOpen) keyboardOverlayState.paletteOpen = false;
};

const closeCheatSheet = () => {
  keyboardOverlayState.cheatSheetOpen = false;
};

const closeAll = () => {
  keyboardOverlayState.paletteOpen = false;
  keyboardOverlayState.cheatSheetOpen = false;
};

export const keyboardOverlayActions = Object.freeze({
  openPalette,
  closePalette,
  togglePalette,
  toggleCheatSheet,
  closeCheatSheet,
  closeAll,
});

export type KeyboardOverlayActions = typeof keyboardOverlayActions;
